import { useState, useEffect } from 'react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { Input } from './ui/Input'
import { Badge } from './ui/Badge'
import { LoadingSpinner } from './ui/LoadingSpinner'
import { useApiMutation, usePolling } from '../hooks/useApi'
import { formatRelativeTime, getStatusIcon } from '../lib/utils'
import type { Customer, BulkOperation, BulkOperationItem } from '../types'

interface BulkOperationsModalProps {
  isOpen: boolean
  onClose: () => void
  customers: Customer[]
  onSuccess: () => void
}

interface BulkOperationConfig {
  operation_type: 'deploy' | 'rollback'
  customer_ids: string[]
  version?: string
  rollback_version?: string
  batch_size: number
  delay_between_batches: number
  failure_threshold: number
}

export function BulkOperationsModal({ isOpen, onClose, customers, onSuccess }: BulkOperationsModalProps) {
  const [step, setStep] = useState<'config' | 'confirm' | 'progress'>('config')
  const [config, setConfig] = useState<BulkOperationConfig>({
    operation_type: 'deploy',
    customer_ids: [],
    version: 'latest',
    batch_size: 5,
    delay_between_batches: 30,
    failure_threshold: 20,
  })
  const [operationId, setOperationId] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Mutations
  const { mutate: startBulkOperation, loading: starting } = useApiMutation<BulkOperation>()

  // Poll operation progress
  const { data: operation, loading: operationLoading } = usePolling<BulkOperation & { items: BulkOperationItem[] }>(
    operationId ? `/bulk/operations/${operationId}` : '',
    2000, // Poll every 2 seconds
    { skip: !operationId }
  )

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('config')
      setConfig({
        operation_type: 'deploy',
        customer_ids: [],
        version: 'latest',
        batch_size: 5,
        delay_between_batches: 30,
        failure_threshold: 20,
      })
      setOperationId(null)
      setErrors({})
    }
  }, [isOpen])

  const validateConfig = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (config.customer_ids.length === 0) {
      newErrors.customers = 'Please select at least one customer'
    }

    if (config.operation_type === 'deploy' && !config.version?.trim()) {
      newErrors.version = 'Version is required for deployments'
    }

    if (config.operation_type === 'rollback' && !config.rollback_version?.trim()) {
      newErrors.rollback_version = 'Rollback version is required'
    }

    if (config.batch_size < 1 || config.batch_size > 20) {
      newErrors.batch_size = 'Batch size must be between 1 and 20'
    }

    if (config.delay_between_batches < 0 || config.delay_between_batches > 300) {
      newErrors.delay_between_batches = 'Delay must be between 0 and 300 seconds'
    }

    if (config.failure_threshold < 0 || config.failure_threshold > 100) {
      newErrors.failure_threshold = 'Failure threshold must be between 0 and 100%'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleStart = async () => {
    if (!validateConfig()) return

    try {
      const result = await startBulkOperation('/bulk/operations', config)
      setOperationId(result.id)
      setStep('progress')
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to start operation' })
    }
  }

  const handleClose = () => {
    if (step === 'progress' && operation?.status === 'completed') {
      onSuccess()
    }
    onClose()
  }

  const selectedCustomers = customers.filter(c => config.customer_ids.includes(c.id))

  const renderConfigStep = () => (
    <div className="space-y-6">
      {/* Operation Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Operation Type
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setConfig(prev => ({ ...prev, operation_type: 'deploy' }))}
            className={`p-3 border rounded-lg text-left transition-colors ${
              config.operation_type === 'deploy'
                ? 'border-blue-500 bg-blue-50 text-blue-900'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="font-medium">Deploy</div>
            <div className="text-sm text-gray-500">Deploy new version to customers</div>
          </button>
          <button
            type="button"
            onClick={() => setConfig(prev => ({ ...prev, operation_type: 'rollback' }))}
            className={`p-3 border rounded-lg text-left transition-colors ${
              config.operation_type === 'rollback'
                ? 'border-blue-500 bg-blue-50 text-blue-900'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <div className="font-medium">Rollback</div>
            <div className="text-sm text-gray-500">Rollback to previous version</div>
          </button>
        </div>
      </div>

      {/* Version */}
      {config.operation_type === 'deploy' ? (
        <Input
          label="Version to Deploy"
          value={config.version || ''}
          onChange={(e) => setConfig(prev => ({ ...prev, version: e.target.value }))}
          error={errors.version}
          placeholder="latest"
          helperText="Git tag, branch, or commit hash"
        />
      ) : (
        <Input
          label="Rollback to Version"
          value={config.rollback_version || ''}
          onChange={(e) => setConfig(prev => ({ ...prev, rollback_version: e.target.value }))}
          error={errors.rollback_version}
          placeholder="v1.0.0"
          helperText="Version to rollback to"
        />
      )}

      {/* Customer Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Customers ({config.customer_ids.length} selected)
        </label>
        <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg">
          <div className="p-2">
            <label className="flex items-center p-2 hover:bg-gray-50 rounded">
              <input
                type="checkbox"
                checked={config.customer_ids.length === customers.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setConfig(prev => ({ ...prev, customer_ids: customers.map(c => c.id) }))
                  } else {
                    setConfig(prev => ({ ...prev, customer_ids: [] }))
                  }
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm font-medium text-gray-900">Select All</span>
            </label>
          </div>
          <div className="border-t border-gray-200">
            {customers.map((customer) => (
              <label key={customer.id} className="flex items-center p-2 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={config.customer_ids.includes(customer.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setConfig(prev => ({ 
                        ...prev, 
                        customer_ids: [...prev.customer_ids, customer.id] 
                      }))
                    } else {
                      setConfig(prev => ({ 
                        ...prev, 
                        customer_ids: prev.customer_ids.filter(id => id !== customer.id) 
                      }))
                    }
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div className="ml-2 flex-1">
                  <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                  <div className="text-xs text-gray-500">{customer.domain || 'No domain'}</div>
                </div>
                <Badge variant={customer.status === 'active' ? 'success' : 'default'} className="ml-2">
                  {customer.status}
                </Badge>
              </label>
            ))}
          </div>
        </div>
        {errors.customers && (
          <p className="mt-1 text-sm text-red-600">{errors.customers}</p>
        )}
      </div>

      {/* Batch Configuration */}
      <div className="grid grid-cols-3 gap-4">
        <Input
          label="Batch Size"
          type="number"
          min="1"
          max="20"
          value={config.batch_size}
          onChange={(e) => setConfig(prev => ({ ...prev, batch_size: parseInt(e.target.value) || 1 }))}
          error={errors.batch_size}
          helperText="Customers per batch"
        />
        <Input
          label="Delay (seconds)"
          type="number"
          min="0"
          max="300"
          value={config.delay_between_batches}
          onChange={(e) => setConfig(prev => ({ ...prev, delay_between_batches: parseInt(e.target.value) || 0 }))}
          error={errors.delay_between_batches}
          helperText="Wait between batches"
        />
        <Input
          label="Failure Threshold (%)"
          type="number"
          min="0"
          max="100"
          value={config.failure_threshold}
          onChange={(e) => setConfig(prev => ({ ...prev, failure_threshold: parseInt(e.target.value) || 0 }))}
          error={errors.failure_threshold}
          helperText="Auto-pause threshold"
        />
      </div>

      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{errors.submit}</p>
        </div>
      )}
    </div>
  )

  const renderConfirmStep = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <span className="text-yellow-400 text-xl">⚠️</span>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Confirm Bulk Operation
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              This will {config.operation_type} {config.customer_ids.length} customer(s). 
              This action cannot be undone.
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex justify-between">
          <span className="text-sm text-gray-600">Operation:</span>
          <span className="text-sm font-medium text-gray-900 capitalize">{config.operation_type}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600">Version:</span>
          <span className="text-sm font-medium text-gray-900">
            {config.operation_type === 'deploy' ? config.version : config.rollback_version}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600">Customers:</span>
          <span className="text-sm font-medium text-gray-900">{config.customer_ids.length}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600">Batch Size:</span>
          <span className="text-sm font-medium text-gray-900">{config.batch_size}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600">Delay:</span>
          <span className="text-sm font-medium text-gray-900">{config.delay_between_batches}s</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600">Failure Threshold:</span>
          <span className="text-sm font-medium text-gray-900">{config.failure_threshold}%</span>
        </div>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-2">Selected Customers:</h4>
        <div className="max-h-32 overflow-y-auto space-y-1">
          {selectedCustomers.map((customer) => (
            <div key={customer.id} className="flex items-center justify-between text-sm">
              <span>{customer.name}</span>
              <Badge variant={customer.status === 'active' ? 'success' : 'default'}>
                {customer.status}
              </Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const renderProgressStep = () => {
    if (!operation) {
      return (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      )
    }

    const progress = operation.total_customers > 0 
      ? (operation.completed_customers / operation.total_customers) * 100 
      : 0

    return (
      <div className="space-y-6">
        {/* Progress Overview */}
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900 mb-2">
            {operation.completed_customers} / {operation.total_customers}
          </div>
          <div className="text-sm text-gray-600 mb-4">
            {operation.status === 'completed' ? 'Operation completed' : 
             operation.status === 'failed' ? 'Operation failed' :
             operation.status === 'paused' ? 'Operation paused' : 'Operation in progress'}
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                operation.status === 'completed' ? 'bg-green-500' :
                operation.status === 'failed' ? 'bg-red-500' :
                operation.status === 'paused' ? 'bg-yellow-500' : 'bg-blue-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>

          <Badge 
            variant={
              operation.status === 'completed' ? 'success' :
              operation.status === 'failed' ? 'error' :
              operation.status === 'paused' ? 'warning' : 'info'
            }
          >
            {getStatusIcon(operation.status)} {operation.status}
          </Badge>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-green-600">{operation.completed_customers}</div>
            <div className="text-xs text-gray-500">Completed</div>
          </div>
          <div>
            <div className="text-lg font-bold text-red-600">{operation.failed_customers}</div>
            <div className="text-xs text-gray-500">Failed</div>
          </div>
          <div>
            <div className="text-lg font-bold text-gray-600">
              {operation.total_customers - operation.completed_customers - operation.failed_customers}
            </div>
            <div className="text-xs text-gray-500">Pending</div>
          </div>
        </div>

        {/* Individual Items */}
        {operation.items && operation.items.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Customer Status</h4>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {operation.items.map((item) => {
                const customer = customers.find(c => c.id === item.customer_id)
                return (
                  <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">
                      {customer?.name || item.customer_id}
                    </span>
                    <div className="flex items-center space-x-2">
                      <Badge 
                        variant={
                          item.status === 'completed' ? 'success' :
                          item.status === 'failed' ? 'error' :
                          item.status === 'in_progress' ? 'info' : 'warning'
                        }
                      >
                        {getStatusIcon(item.status)} {item.status}
                      </Badge>
                      {item.completed_at && (
                        <span className="text-xs text-gray-500">
                          {formatRelativeTime(item.completed_at)}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      title={
        step === 'config' ? 'Bulk Operations' :
        step === 'confirm' ? 'Confirm Operation' : 'Operation Progress'
      }
      size="lg"
    >
      <div className="space-y-6">
        {step === 'config' && renderConfigStep()}
        {step === 'confirm' && renderConfirmStep()}
        {step === 'progress' && renderProgressStep()}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          {step === 'config' && (
            <>
              <div />
              <div className="flex items-center space-x-3">
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={() => setStep('confirm')} disabled={config.customer_ids.length === 0}>
                  Next
                </Button>
              </div>
            </>
          )}

          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={() => setStep('config')}>
                Back
              </Button>
              <div className="flex items-center space-x-3">
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={handleStart} loading={starting} variant="danger">
                  Start {config.operation_type}
                </Button>
              </div>
            </>
          )}

          {step === 'progress' && (
            <>
              <div />
              <Button onClick={handleClose}>
                {operation?.status === 'completed' ? 'Done' : 'Close'}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}